import React, { useState, useRef } from 'react';
import { 
  Box, Button, Typography, Alert, List, ListItem, 
  ListItemIcon, ListItemText, LinearProgress, useTheme 
} from '@mui/material';
import { Upload, CheckCircle, Error as ErrorIcon, Close, Download } from '@mui/icons-material';
import { useFirestore } from '../../hooks/useFirestore';

const ContactImport = ({ onSuccess, onCancel }) => {
  const theme = useTheme();
  const { add, batch } = useFirestore('contacts');
  const fileInputRef = useRef();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    setError('');
    setResults(null);

    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].toLowerCase().split(',');
      
      const contacts = lines.slice(1).map(line => {
        const values = line.split(',');
        const contact = {};
        headers.forEach((header, i) => {
          contact[header.trim()] = values[i]?.trim() || '';
        });
        return contact;
      });

      let success = 0;
      let errors = 0;

      for (let i = 0; i < contacts.length; i++) {
        try {
          await add({
            firstName: contacts[i].nombre || contacts[i].firstname || '',
            lastName: contacts[i].apellidos || contacts[i].lastname || '',
            email: contacts[i].email || contacts[i].correo || '',
            phone: contacts[i].telefono || contacts[i].phone || '',
            mobile: contacts[i].movil || contacts[i].mobile || '',
            company: contacts[i].empresa || contacts[i].company || '',
            position: contacts[i].cargo || contacts[i].position || ''
          });
          success++;
        } catch (err) {
          errors++;
        }
        setProgress(Math.round(((i + 1) / contacts.length) * 100));
      }

      setResults({ total: contacts.length, success, errors });
      if (success > 0 && errors === 0) {
        setTimeout(onSuccess, 1500);
      }
    } catch (err) {
      setError('Error al leer el archivo. Verifica que sea un CSV válido.');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const csvContent = "nombre,apellidos,email,telefono,movil,empresa,cargo\nJuan,Pérez,juan@email.com,911234567,612345678,MiEmpresa,Gerente";
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plantilla_contactos.csv';
    a.click();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography 
          variant="h6"
          sx={{
            color: theme.palette.primary.main,
            fontWeight: 600,
          }}
        >
          Importar Contactos
        </Typography>
        <Button 
          onClick={onCancel}
          sx={{ color: theme.palette.text.secondary }}
        >
          <Close />
        </Button>
      </Box>

      <Alert 
        severity="info" 
        sx={{ 
          mb: 3,
          borderRadius: '12px',
          backgroundColor: theme.palette.mode === 'dark' 
            ? 'rgba(0, 175, 163, 0.1)' 
            : 'rgba(0, 95, 101, 0.05)',
          border: '1px solid',
          borderColor: theme.palette.primary.main,
        }}
      >
        Sube un archivo CSV con las columnas: nombre, apellidos, email, telefono, movil, empresa, cargo
        <Button 
          size="small" 
          startIcon={<Download />} 
          onClick={downloadTemplate} 
          sx={{ 
            mt: 1, 
            display: 'block',
            color: theme.palette.primary.main,
          }}
        >
          Descargar plantilla
        </Button>
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{error}</Alert>}

      {results ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          {results.errors === 0 ? (
            <CheckCircle sx={{ fontSize: 64, color: theme.palette.success.main, mb: 2 }} />
          ) : (
            <ErrorIcon sx={{ fontSize: 64, color: theme.palette.warning.main, mb: 2 }} />
          )}
          <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
            {results.success} de {results.total} contactos importados
          </Typography>
          {results.errors > 0 && (
            <Typography color="error">{results.errors} errores</Typography>
          )}
        </Box>
      ) : (
        <Box>
          <input 
            type="file" 
            accept=".csv" 
            ref={fileInputRef} 
            onChange={handleFile} 
            hidden 
          />
          <Box 
            sx={{ 
              border: '2px dashed', 
              borderColor: theme.palette.primary.main, 
              borderRadius: 2, 
              p: 6, 
              textAlign: 'center', 
              cursor: 'pointer',
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(0, 175, 163, 0.05)' 
                : 'rgba(0, 95, 101, 0.02)',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(0, 175, 163, 0.1)' 
                  : 'rgba(0, 95, 101, 0.05)',
                borderColor: theme.palette.secondary.main,
              },
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 2 }} />
            <Typography sx={{ color: theme.palette.text.primary }}>
              Haz clic para seleccionar archivo CSV
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Formatos aceptados: .csv
            </Typography>
          </Box>
          {loading && (
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ 
                mt: 2,
                height: 8,
                borderRadius: 4,
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(0, 175, 163, 0.2)' 
                  : 'rgba(0, 95, 101, 0.1)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: 4,
                },
              }} 
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default ContactImport;